import React, { useCallback, useEffect, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  IconButton,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  Snackbar,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  Tooltip,
  Typography,
} from '@mui/material';
import {
  Add as AddIcon,
  Delete as DeleteIcon,
  Edit as EditIcon,
  Refresh as RefreshIcon,
} from '@mui/icons-material';
import { getDogAttributes, deleteDogAttribute } from '../services/dogAttributeApi';
import DogAttributeForm from './DogAttributeForm';

const TYPE_OPTIONS = [
  { label: 'All', value: '' },
  { label: 'BREED', value: 'BREED' },
  { label: 'SIZE', value: 'SIZE' },
  { label: 'ENERGY_LEVEL', value: 'ENERGY_LEVEL' },
];

const TYPE_CHIP_COLORS = {
  BREED:        { bg: '#EDE9FE', text: '#5B21B6' },
  SIZE:         { bg: '#E0F2FE', text: '#075985' },
  ENERGY_LEVEL: { bg: '#FEF3C7', text: '#92400E' },
};

function TypeBadge({ type }) {
  const colors = TYPE_CHIP_COLORS[type] || { bg: '#F3F4F6', text: '#374151' };
  return (
    <Box
      component="span"
      sx={{
        display: 'inline-block',
        px: 1,
        py: 0.25,
        borderRadius: 1,
        bgcolor: colors.bg,
        color: colors.text,
        fontWeight: 600,
        fontSize: '0.72rem',
      }}
    >
      {type}
    </Box>
  );
}

const DogAttributesManagement = () => {
  // Filter
  const [typeFilter, setTypeFilter] = useState('');

  // Data
  const [attributes, setAttributes]         = useState([]);
  const [loading, setLoading]               = useState(false);
  const [error, setError]                   = useState('');

  // Pagination (client-side — the endpoint returns the full list)
  const [page, setPage]                     = useState(0);
  const [rowsPerPage, setRowsPerPage]       = useState(20);

  // Form dialog
  const [formOpen, setFormOpen]             = useState(false);
  const [editingAttribute, setEditingAttribute] = useState(null);

  // Delete dialog
  const [deleteOpen, setDeleteOpen]         = useState(false);
  const [deletingAttr, setDeletingAttr]     = useState(null);
  const [deleteBusy, setDeleteBusy]         = useState(false);
  const [deleteError, setDeleteError]       = useState('');

  // Toast
  const [toast, setToast]                   = useState({ open: false, message: '', severity: 'success' });

  const showToast = (message, severity = 'success') =>
    setToast({ open: true, message, severity });

  const fetchAttributes = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const data = await getDogAttributes(typeFilter || undefined);
      setAttributes(Array.isArray(data) ? data : []);
      setPage(0);
    } catch (err) {
      setError('Failed to load dog attributes. Please try again.');
    } finally {
      setLoading(false);
    }
  }, [typeFilter]);

  useEffect(() => {
    fetchAttributes();
  }, [fetchAttributes]);

  // Paginate client-side
  const paginated = attributes.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage);

  // Form handlers
  const handleOpenCreate = () => {
    setEditingAttribute(null);
    setFormOpen(true);
  };
  const handleOpenEdit = (attr) => {
    setEditingAttribute(attr);
    setFormOpen(true);
  };
  const handleFormClose = () => setFormOpen(false);
  const handleSaved = (saved) => {
    setAttributes((prev) => {
      const idx = prev.findIndex((a) => a.id === saved.id);
      if (idx >= 0) {
        const next = [...prev];
        next[idx] = saved;
        return next;
      }
      return [saved, ...prev];
    });
    showToast(editingAttribute ? 'Attribute updated.' : 'Attribute created.');
  };

  // Delete handlers
  const handleOpenDelete = (attr) => {
    setDeletingAttr(attr);
    setDeleteError('');
    setDeleteOpen(true);
  };
  const handleCloseDelete = () => {
    if (!deleteBusy) setDeleteOpen(false);
  };
  const handleConfirmDelete = async () => {
    if (!deletingAttr) return;
    setDeleteBusy(true);
    setDeleteError('');
    try {
      await deleteDogAttribute(deletingAttr.id);
      setAttributes((prev) => prev.filter((a) => a.id !== deletingAttr.id));
      setDeleteOpen(false);
      showToast('Attribute deleted.');
    } catch (err) {
      // 409 → attribute is in use
      if (err?.message?.includes('409')) {
        setDeleteError('Cannot delete: this attribute is used by existing dogs or groups.');
      } else {
        setDeleteError(err?.message || 'Failed to delete. Please try again.');
      }
    } finally {
      setDeleteBusy(false);
    }
  };

  return (
    <Box>
      {/* Header row */}
      <Stack
        direction={{ xs: 'column', sm: 'row' }}
        spacing={2}
        alignItems={{ xs: 'stretch', sm: 'center' }}
        justifyContent="space-between"
        sx={{ mb: 2 }}
      >
        <Box>
          <Typography variant="h5" fontWeight={700}>Dog Attributes</Typography>
          <Typography variant="body2" color="text.secondary">
            Manage breed, size, and energy level attributes for dogs and groups.
          </Typography>
        </Box>
        <Stack direction="row" spacing={1}>
          <Button
            startIcon={<RefreshIcon />}
            onClick={fetchAttributes}
            disabled={loading}
          >
            Refresh
          </Button>
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={handleOpenCreate}
          >
            Add Attribute
          </Button>
        </Stack>
      </Stack>

      {/* Filter toolbar */}
      <Paper
        elevation={0}
        sx={{ p: 2, mb: 2, border: '1px solid', borderColor: 'divider', borderRadius: 2 }}
      >
        <FormControl size="small" sx={{ minWidth: 200 }}>
          <InputLabel>Type</InputLabel>
          <Select
            value={typeFilter}
            label="Type"
            onChange={(e) => { setTypeFilter(e.target.value); setPage(0); }}
          >
            {TYPE_OPTIONS.map((o) => (
              <MenuItem key={o.value} value={o.value}>{o.label}</MenuItem>
            ))}
          </Select>
        </FormControl>
      </Paper>

      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

      {/* Table */}
      <Paper elevation={0} sx={{ border: '1px solid', borderColor: 'divider', borderRadius: 2, overflow: 'hidden' }}>
        <Box sx={{ px: 2, py: 1.5, borderBottom: '1px solid', borderColor: 'divider', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <Typography variant="subtitle2" color="text.secondary">
            {loading ? 'Loading…' : `${attributes.length.toLocaleString()} attribute${attributes.length !== 1 ? 's' : ''}`}
          </Typography>
          {loading && <CircularProgress size={16} />}
        </Box>

        <TableContainer>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell sx={{ width: 70 }}>ID</TableCell>
                <TableCell sx={{ width: 140 }}>Type</TableCell>
                <TableCell>English</TableCell>
                <TableCell>Hebrew</TableCell>
                <TableCell>Spanish</TableCell>
                <TableCell align="right" sx={{ width: 100 }}>Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {!loading && paginated.length === 0 && (
                <TableRow>
                  <TableCell colSpan={6}>
                    <Box sx={{ py: 5, textAlign: 'center' }}>
                      <Typography color="text.secondary">
                        {typeFilter ? 'No attributes match the current filter.' : 'No attributes found.'}
                      </Typography>
                    </Box>
                  </TableCell>
                </TableRow>
              )}
              {paginated.map((attr) => (
                <TableRow key={attr.id} hover sx={{ '&:last-child td': { borderBottom: 0 } }}>
                  <TableCell>
                    <Typography variant="body2" color="text.secondary">#{attr.id}</Typography>
                  </TableCell>
                  <TableCell>
                    <TypeBadge type={attr.type} />
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2" fontWeight={500}>{attr.nameEn || '—'}</Typography>
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2" color="text.secondary" dir="rtl">
                      {attr.nameHe || '—'}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2" color="text.secondary">{attr.nameEs || '—'}</Typography>
                  </TableCell>
                  <TableCell align="right">
                    <Stack direction="row" spacing={0.5} justifyContent="flex-end">
                      <Tooltip title="Edit">
                        <IconButton size="small" color="primary" onClick={() => handleOpenEdit(attr)}>
                          <EditIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                      <Tooltip title="Delete">
                        <IconButton size="small" color="error" onClick={() => handleOpenDelete(attr)}>
                          <DeleteIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    </Stack>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>

        <TablePagination
          component="div"
          count={attributes.length}
          page={page}
          onPageChange={(_, newPage) => setPage(newPage)}
          rowsPerPage={rowsPerPage}
          onRowsPerPageChange={(e) => { setRowsPerPage(+e.target.value); setPage(0); }}
          rowsPerPageOptions={[10, 20, 50, 100]}
          sx={{ borderTop: '1px solid', borderColor: 'divider' }}
        />
      </Paper>

      {/* Create / Edit dialog */}
      <DogAttributeForm
        open={formOpen}
        attribute={editingAttribute}
        onClose={handleFormClose}
        onSaved={handleSaved}
      />

      {/* Delete confirmation dialog */}
      <Dialog open={deleteOpen} onClose={handleCloseDelete} maxWidth="xs" fullWidth>
        <DialogTitle>Delete attribute</DialogTitle>
        <DialogContent>
          <Typography variant="body2" sx={{ mb: deleteError ? 2 : 0 }}>
            Permanently delete{' '}
            <strong>{deletingAttr?.nameEn || `#${deletingAttr?.id}`}</strong>
            {' '}({deletingAttr?.type})?
          </Typography>
          {deleteError && (
            <Alert severity="error" sx={{ mt: 2 }}>{deleteError}</Alert>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDelete} disabled={deleteBusy}>Cancel</Button>
          <Button
            variant="contained"
            color="error"
            onClick={handleConfirmDelete}
            disabled={deleteBusy}
          >
            {deleteBusy ? 'Deleting…' : 'Delete'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Success / error toast */}
      <Snackbar
        open={toast.open}
        autoHideDuration={4000}
        onClose={() => setToast((t) => ({ ...t, open: false }))}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert
          onClose={() => setToast((t) => ({ ...t, open: false }))}
          severity={toast.severity}
          sx={{ width: '100%' }}
        >
          {toast.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default DogAttributesManagement;
